import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {TEXT_FIELD_BACKGROUND_COLOR, TEXT_FIELD_BORDER} from 'chemistry/molecules/MoleculeConstants';
import {MonomerCapGroup} from 'graphql-client/__generated/graphqlGlobalTypes';
import {MonomerCapGroupWithWildcard, WILDCARD_CAP_GROUP_SYMBOL} from 'molbio/monomers/MonomerConstants';
import {FONT_PROXIMA_13} from 'styles/FontConstants';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import Menu from 'ui-library/menus/Menu';
import {MenuWidth} from 'ui-library/menus/MenuConstants';
import Option from 'ui-library/menus/Option';

interface MonomerConfigureAttachmentPointsSectionProps {
  attachmentPointsInput: Array<MonomerCapGroup | null> | null;
  onChangeAttachmentPointsInput: (attachmentPointsInput: Array<MonomerCapGroup | null> | null) => void;
  calculatedAttachmentPoints: Array<MonomerCapGroupWithWildcard> | null;
  allowNonWildcardCapGroupSelection?: boolean;
}

export default function MonomerConfigureAttachmentPointsSection({
  attachmentPointsInput,
  onChangeAttachmentPointsInput,
  calculatedAttachmentPoints,
  allowNonWildcardCapGroupSelection,
}: MonomerConfigureAttachmentPointsSectionProps): JSX.Element {
  const textFieldWithLabel = (text: string | null, label: string): JSX.Element => (
    <div>
      <div className={css(styles.textFieldSubheader)}>
        <span>{label}</span>
      </div>
      <div className={css(styles.grayTextField)}>
        <span>{text}</span>
      </div>
    </div>
  );
  return (
    <div>
      {calculatedAttachmentPoints != null && calculatedAttachmentPoints.length > 0 ? (
        calculatedAttachmentPoints.map((calculatedAttachmentPoint, i) => {
          let selectedCappingGroup: string | null = null;
          if (calculatedAttachmentPoint === WILDCARD_CAP_GROUP_SYMBOL) {
            if (attachmentPointsInput?.[i] != null) {
              selectedCappingGroup = attachmentPointsInput[i];
            }
          } else {
            selectedCappingGroup = calculatedAttachmentPoint;
          }
          return (
            <div className={css(styles.attachmentPointInputs)} key={i}>
              <div className={css(styles.flex1)}>{textFieldWithLabel(`R${i + 1}`, 'Attachment point')}</div>
              <div className={css(styles.flex1)}>
                <div className={css(styles.textFieldSubheader)}>
                  <span>Capping group</span>
                </div>
                <SelectDropdown
                  alwaysDropDown={true}
                  displayName={selectedCappingGroup ?? 'Select group'}
                  disabled={
                    calculatedAttachmentPoint !== WILDCARD_CAP_GROUP_SYMBOL &&
                    !allowNonWildcardCapGroupSelection
                  }
                  menu={
                    <Menu value={selectedCappingGroup} width={MenuWidth.FULL}>
                      {Object.values(MonomerCapGroup).map((group) => (
                        <Option
                          key={group}
                          value={group}
                          isSelected={selectedCappingGroup === group}
                          onSelect={(newCappingGroup: MonomerCapGroup) => {
                            onChangeAttachmentPointsInput(
                              attachmentPointsInput != null
                                ? [
                                    ...attachmentPointsInput.slice(0, i),
                                    newCappingGroup,
                                    ...attachmentPointsInput.slice(i + 1),
                                  ]
                                : [newCappingGroup]
                            );
                          }}
                        >
                          {group}
                        </Option>
                      ))}
                    </Menu>
                  }
                  dataTestAttrs={{
                    component: 'MonomerConfigureAttachmentPointsSection',
                    element: 'attachmentPointDropdown',
                  }}
                />
              </div>
            </div>
          );
        })
      ) : (
        <div className={css(styles.attachmentPointInputs)}>
          <div className={css(styles.flex1)}>{textFieldWithLabel('', 'Attachment point')}</div>
          <div className={css(styles.flex1)}>
            <div className={css(styles.textFieldSubheader)}>
              <span>Capping group</span>
            </div>
            <SelectDropdown displayName="Select group" disabled={true} menu={<div />} />
          </div>
        </div>
      )}
    </div>
  );
}

const styles = StyleSheet.create({
  attachmentPointInputs: {
    display: 'flex',
    flexDirection: 'row',
    gap: 13,
    marginBottom: 16,
  },
  textFieldSubheader: {
    fontWeight: 'bold',
    marginBottom: 4,
    ...FONT_PROXIMA_13,
  },
  flex1: {
    flex: 1,
  },
  grayTextField: {
    padding: '3px 10px',
    background: TEXT_FIELD_BACKGROUND_COLOR,
    border: TEXT_FIELD_BORDER,
    boxSizing: 'border-box',
    borderRadius: '4px',
    height: 28,
    ...FONT_PROXIMA_13,
  },
});
